import { useState, useRef } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { localizeNotificationContent } from "../utils/notificationI18n";
import {
  bildirimEkleService,
  bildirimHerkeseYazService,
  bildirimleriDinleService,
  bildirimOkunduIsaretle,
  bildirimKaynakOkunduIsaretle,
  sohbetBildirimleriniSilService,
  isDismissed,
} from "../services/bildirimService";
import {
  isAndroidRestrictedNotification,
  normalizeAndroidPage,
} from "../utils/platformFeatures";
import { resolveNotificationNavigation } from "../utils/notificationNavigation";

// Backend'den gelen bildirim nesnelerini normalize et
const normalizeNotif = (n, familyNameMap = {}) => {
  const familyId = n.familyId || n.family_id || null;
  // Sorun 5 düzeltmesi: familyId → familyName map'ten bul
  const ailenin = familyId ? (familyNameMap[String(familyId)] || null) : null;
  const suppressFamilyName = n.suppressFamilyName === true || n.data?.suppressFamilyName === true || n.data?.globalNotification === true;
  // Tüm kategori field'larını normalize et — BildirimPopup bunları kullanır
  const bildirimType = n.bildirimType || n.bildirim_type || null;
  const tip = n.tip || null;
  const baslikKey = n.baslikKey || n.baslik_key || null;
  return {
    ...n,
    okundu:    n.okundu ?? !!n.read_at ?? false,
    createdAt: n.createdAt || n.created_at || null,
    kaynakId:  n.kaynakId || n.source_id || null,
    familyId,
    module: n.module || n.modul || n.data?.module || null,
    source: n.source || n.kaynak || n.data?.source || n.data?.kaynak || null,
    suppressFamilyName,
    familyName: suppressFamilyName ? null : (n.familyName || n.aileAdi || ailenin || null),
    bildirimType,
    tip,
    baslikKey,
  };
};

const isChatNotification = (n = {}) => {
  const type = String(n.type || n.bildirimTipi || n.tip || "").toLowerCase();
  const module = String(n.module || n.modul || n.data?.module || "").toLowerCase();
  const source = String(n.kaynak || n.source || n.data?.kaynak || n.data?.source || "").toLowerCase();

  // Yönetim / sistem / aile duyuruları yanlışlıkla type=chat gelse bile sohbet sekmesine girmemeli.
  if (["notificationadmin", "familynotification", "systemannouncement", "familyannouncement"].includes(source)) return false;
  if (["system", "systemannouncement", "familyannouncement", "scheduled", "notification"].includes(type)) return false;

  return type === "chat" || module === "chat" || source === "chat" || source === "chatmessage";
};

export function useNotifications({ navigateTo }) {
  const { lang } = useLanguage();

  const metniDileUyarla = (notif = {}) =>
    localizeNotificationContent(
      {
        baslik: notif.baslik,
        aciklama: notif.aciklama,
        baslikKey: notif.baslikKey,
        baslikVars: notif.baslikVars,
        aciklamaKey: notif.aciklamaKey,
        aciklamaVars: notif.aciklamaVars,
        baslikI18n: notif.baslikI18n || notif.baslik_i18n || notif.translations?.baslik,
        aciklamaI18n: notif.aciklamaI18n || notif.aciklama_i18n || notif.translations?.aciklama,
        familyName: notif.familyName || notif.aileAdi,
        type: notif.type,
        kaynak: notif.kaynak,
      },
      lang
    );

  const [inAppNotifs, setInAppNotifs] = useState([]);
  const inAppNotifsRef = useRef([]);

  const [bildirimPopupAcik, setBildirimPopupAcik] = useState(false);
  const [bildirimChatRoomId, setBildirimChatRoomId] = useState(null);
  const NOTIF_CACHE_KEY    = "fp:notifCache";
  const GORULЕН_IDS_KEY    = "fp:seenNotifIds";

  const notificationIsDismissed = (n = {}) =>
    isDismissed(n.id) || isDismissed(n._globalNotificationId) || isDismissed(n.firebaseId);

  const readNotifCache = () => {
    try {
      const r = localStorage.getItem(NOTIF_CACHE_KEY);
      const items = r ? JSON.parse(r) : null;
      return Array.isArray(items)
        ? items.filter((n) => !notificationIsDismissed(n) && !isAndroidRestrictedNotification(n))
        : null;
    }
    catch { return null; }
  };
  const writeNotifCache = (items) => {
    try {
      localStorage.setItem(
        NOTIF_CACHE_KEY,
        JSON.stringify((items || []).filter((n) => !isAndroidRestrictedNotification(n)))
      );
    } catch {}
  };
  // Görülen bildirim ID'lerini persist et — uygulama restart'ta "eski = yeni" toast önlenir
  const readSeenIds = () => {
    try { const r = localStorage.getItem(GORULЕН_IDS_KEY); return r ? new Set(JSON.parse(r)) : new Set(); }
    catch { return new Set(); }
  };
  const writeSeenIds = (set) => {
    try {
      // Son 1000 ID'yi sakla. Her bildirim 2 format (gid + bareId) ile kaydedildiği için
      // 500 bildirime karşılık 1000 entry gerekebilir. localStorage yükü minimal (<50KB).
      const arr = Array.from(set).slice(-1000);
      localStorage.setItem(GORULЕН_IDS_KEY, JSON.stringify(arr));
    } catch {}
  };

  const [merkeziBildirimler, setMerkeziBildirimler] = useState(() => readNotifCache() || []);
  const [merkeziChatBildirimler, setMerkeziChatBildirimler] = useState([]);

  const [hatirlaticiGoster, setHatirlaticiGoster] = useState(false);
  const [popupBildirimler, setPopupBildirimler] = useState([]);

  const ilkBildirimSnapshotRef = useRef(false);
  // Görülen ID'leri localStorage'dan başlat — kill+restart'ta toast tekrarını önler
  const gorulenBildirimIdsRef  = useRef(readSeenIds());

  // ─── Anlık bildirim göster / kaldır ────────────────────────────────────────
  const bildirimGoster = (baslik, aciklama, type = "default", ekstra = {}) => {
    if (isAndroidRestrictedNotification({ type, ...ekstra })) return;
    const id = Date.now() + Math.random();
    const yeni = { id, baslik, aciklama, type, ...ekstra };
    inAppNotifsRef.current = [...inAppNotifsRef.current, yeni];
    setInAppNotifs([...inAppNotifsRef.current]);
  };

  const bildirimKaldir = (id) => {
    inAppNotifsRef.current = inAppNotifsRef.current.filter((n) => n.id !== id);
    setInAppNotifs([...inAppNotifsRef.current]);
  };

  // ─── Chat oda bildirimlerini temizle ───────────────────────────────────────
  const chatOdaBildirimleriniTemizle = (roomId, familyId, userUid) => {
    if (!roomId || !familyId || !userUid) return;
    setMerkeziChatBildirimler((prev) =>
      prev.filter((n) => !(n.type === "chat" && n.kaynakId === roomId))
    );
    sohbetBildirimleriniSilService(familyId, userUid, roomId).catch(() => {});
  };

  // ─── Hedef sayfa bulma ─────────────────────────────────────────────────────
  const bildirimHedefSayfaBul = (notif) => {
    const resolved = resolveNotificationNavigation(notif);
    if (resolved.page) return resolved.page;

    const metin = `${notif?.baslik || ""} ${notif?.aciklama || ""}`.toLocaleLowerCase("tr-TR");
    if (metin.includes("görev") || metin.includes("gorev"))   return "gorev";
    if (metin.includes("etkinlik") || metin.includes("takvim") || metin.includes("randevu")) return "takvim";
    if (metin.includes("ilaç") || metin.includes("sağlık"))   return "saglik";
    if (metin.includes("belge") || metin.includes("doküman")) return "belgeler";
    if (metin.includes("alışveriş"))                          return "alisveris";
    if (metin.includes("harcama"))                            return "harcama";
    if (metin.includes("finans") || metin.includes("ödeme"))  return "finans";
    return "ana";
  };

  // ─── Bildirime tıklayınca sayfaya git ─────────────────────────────────────
  const bildirimdenGit = (notif, familyId, userUid) => {
    if (!notif) return;
    if (!String(notif.id || "").startsWith("local-") && notif.id) {
      bildirimOkunduIsaretle(notif.familyId || familyId, userUid, notif.id).catch(() => {});
    }
    if (notif.kaynakId) {
      bildirimKaynakOkunduIsaretle(notif.familyId || familyId, userUid, notif.kaynakId).catch(() => {});
    }
    if (isAndroidRestrictedNotification(notif)) {
      setBildirimPopupAcik(false);
      navigateTo("ana");
      return;
    }
    const navigation = resolveNotificationNavigation(notif);
    if (isChatNotification(notif) || navigation.page === "chat") {
      const roomId = navigation.roomId;
      if (roomId) {
        setBildirimChatRoomId(roomId);
        setMerkeziChatBildirimler((prev) =>
          prev.filter((n) => !(n.type === "chat" && n.kaynakId === roomId))
        );
        sohbetBildirimleriniSilService(notif.familyId || familyId, userUid, roomId).catch(() => {});
      }
      setBildirimPopupAcik(false);
      navigateTo("chat");
      return;
    }
    const hedefSayfa = navigation.page || bildirimHedefSayfaBul(notif);
    if (!hedefSayfa) return;
    const popupAcikKalsin =
      hedefSayfa === "ana" &&
      (["familyAnnouncement", "system", "systemAnnouncement", "scheduled"].includes(notif.type) ||
        ["familyNotification", "notificationAdmin", "scheduledNotification"].includes(notif.kaynak));
    setBildirimPopupAcik(popupAcikKalsin);
    navigateTo(hedefSayfa);
    const birthdayOccurrenceId = navigation.occurrenceId || notif?.data?.occurrenceId || notif?.occurrenceId ||
      (["birthdayAutomation", "birthdayWish"].includes(notif?.kaynak || notif?.source) ? notif?.kaynakId : null);
    if ((notif?.type === "birthday" || navigation.action === "birthday") && birthdayOccurrenceId) {
      window.setTimeout(() => window.dispatchEvent(new CustomEvent("fp:openBirthday", {
        detail: { occurrenceId: birthdayOccurrenceId },
      })), 120);
    }
    if (navigation.action === "familyStatus") {
      window.setTimeout(() => window.dispatchEvent(new CustomEvent("fp:openFamilyStatus")), 120);
    }
    if (navigation.action === "familyCard") {
      window.setTimeout(() => window.dispatchEvent(new CustomEvent("fp:openFamilyCard")), 120);
    }
  };

  // ─── Push URL yönlendirmesi ────────────────────────────────────────────────
  const pushUrlYonlendir = (url, familyId, userUid) => {
    if (!url) return false;
    const {
      page: hedefSayfa,
      roomId,
      sourceId,
      action,
      occurrenceId,
    } = resolveNotificationNavigation({ url });
    if (!hedefSayfa) return false;
    if (sourceId) bildirimKaynakOkunduIsaretle(familyId, userUid, sourceId).catch(() => {});
    if (hedefSayfa === "chat" && roomId) {
      setBildirimChatRoomId(roomId);
      chatOdaBildirimleriniTemizle(roomId, familyId, userUid);
    }
    if (hedefSayfa === "ana" && sourceId) setBildirimPopupAcik(true);
    const uygulamaSayfasi = hedefSayfa === "ozelTakvim" ? "saglik" : hedefSayfa;
    const normalizedSayfa = normalizeAndroidPage(uygulamaSayfasi, "ana");
    navigateTo(normalizedSayfa);
    if (action === "familyStatus") {
      window.setTimeout(() => window.dispatchEvent(new CustomEvent("fp:openFamilyStatus")), 120);
    }
    if (action === "familyCard") {
      window.setTimeout(() => window.dispatchEvent(new CustomEvent("fp:openFamilyCard")), 120);
    }
    if (action === "birthday" && occurrenceId) {
      window.setTimeout(() => window.dispatchEvent(new CustomEvent("fp:openBirthday", {
        detail: { occurrenceId },
      })), 150);
    }
    if (window.history?.replaceState) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    return true;
  };

  // ─── Polling ile bildirimleri dinle ───────────────────────────────────────
  // Sorun 5: families listesinden familyId→familyName map'i kur
  const bildirimlereDinle = (familyIdOrIds, userUid, families = []) => {
    if (!familyIdOrIds || !userUid) return;

    // Merkezi bildirim listesi yalnızca aktif aileyi dinler. Önceden profil içindeki
    // tüm familyIds burada ayrı polling'e çevriliyordu; bu davranış başlangıçta N aile
    // kadar istek üretip kritik dashboard çağrılarını düşürebiliyordu.
    const activeFamilyId = String(
      Array.isArray(familyIdOrIds) ? familyIdOrIds[0] : familyIdOrIds
    || "").trim();
    if (!activeFamilyId) return;
    const familyIds = [activeFamilyId];

    // familyId → familyName eşleşmesi
    // families dizisi: [{ familyId, familyName, firebase_family_id, name, ... }]
    const familyNameMap = {};
    (families || []).forEach((f) => {
      const fid = f.familyId || f.firebase_family_id || f.id || "";
      const name = f.familyName || f.name || "";
      if (fid && name) familyNameMap[String(fid)] = name;
    });

    ilkBildirimSnapshotRef.current = false;
    // gorulenBildirimIdsRef'i SIFIRLAMIYORUZ — localStorage'dan önceki oturumun
    // ID'leri yüklü, bunları koruyarak eski bildirimlerin toast çıkarmasını önleriz.

    // Daha önce bu familyIds kümesi için zaten snapshot gelmişse (örn. aynı oturumda
    // bildirimlereDinle yeniden çağrıldıysa) hemen true'ya al — gereksiz bekleme yok.
    const expectedFamilyCount = familyIds.length;
    const snapshotsByFamily = new Map();

    const refreshMergedNotifications = () => {
      const hamBildirimler = Array.from(snapshotsByFamily.entries())
        .flatMap(([fid, list]) =>
          (list || []).map((n) => ({
            ...normalizeNotif(n, familyNameMap),
            _familyScopeId:      fid,
            _globalNotificationId: `${fid}::${n.id}`,
          }))
        );

      // family_id NULL olan sistem/admin bildirimleri her aile sorgusunda gelebilir.
      // Aynı backend bildirimi bildirim merkezinde ve sohbette tekrar tekrar görünmesin diye id bazlı tekilleştir.
      const tekilMap = new Map();
      hamBildirimler.forEach((n) => {
        const key = n.id || n.firebaseId || n._globalNotificationId;
        if (!key || !tekilMap.has(key)) tekilMap.set(key, n);
      });

      const tumBildirimler = Array.from(tekilMap.values())
        .filter((n) => !notificationIsDismissed(n))
        .sort((a, b) => {
          const ms = (v) => {
            if (!v) return 0;
            if (typeof v.toMillis === "function") return v.toMillis();
            if (typeof v.toDate  === "function") return v.toDate().getTime();
            const p = new Date(v).getTime();
            return Number.isFinite(p) ? p : 0;
          };
          return ms(b.createdAt) - ms(a.createdAt);
        });

      const chatOlanlar = tumBildirimler.filter(isChatNotification);
      const digerler    = tumBildirimler.filter((n) => !isChatNotification(n) && !isAndroidRestrictedNotification(n));

      // Her bildirimi "görüldü" olarak işaretle — hem yeni hem eski format kaydedilir.
      // Eski oturumlarda sadece n.id kaydedilmişti; yeni format ${fid}::${n.id} şeklinde.
      // Her ikisini de kaydetmek kill+restart sonrası format uyumsuzluğunu önler.
      const markAllSeen = (list) => {
        list.forEach((n) => {
          if (n._globalNotificationId) gorulenBildirimIdsRef.current.add(n._globalNotificationId);
          if (n.id) gorulenBildirimIdsRef.current.add(String(n.id));
        });
      };

      if (!ilkBildirimSnapshotRef.current) {
        if (snapshotsByFamily.size < expectedFamilyCount) {
          // Henüz tüm aile snapshot'ları gelmedi — ID'leri kaydet, cache'deki veriyi göster
          markAllSeen(tumBildirimler);
          // State'i güncelle: cache'deki tam veriyi zaten gösteriyor, tekrar yazma
          return;
        }
        // Tüm snapshot'lar geldi ama boş → offline/hata; gerçek veri bekle
        if (tumBildirimler.length === 0) return;

        markAllSeen(tumBildirimler);
        writeSeenIds(gorulenBildirimIdsRef.current);
        ilkBildirimSnapshotRef.current = true;
      } else {
        const yeniBildirimler = tumBildirimler.filter((n) => {
          // Her iki formatı da kontrol et — eski oturumda hangisi kaydedilmişse tutsun
          const gid = n._globalNotificationId || n.id;
          const bareId = String(n.id || "");
          return (
            n.okundu !== true &&
            !gorulenBildirimIdsRef.current.has(gid) &&
            !gorulenBildirimIdsRef.current.has(bareId) &&
            !isChatNotification(n)
          );
        });
        yeniBildirimler.forEach((n) => {
          markAllSeen([n]);
          const metin = metniDileUyarla(n);
          const toastBaslik = n.familyName
            ? `[${n.familyName}] ${metin.baslik}`
            : metin.baslik;
          bildirimGoster(toastBaslik, metin.aciklama, n.type || n.module || "default", {
            ...n,
            baslik: toastBaslik,
            aciklama: metin.aciklama,
            id: n.id,
          });
        });
        markAllSeen(tumBildirimler);
        // Görülen ID'leri persist et
        writeSeenIds(gorulenBildirimIdsRef.current);
      }

      const yeniDiger = digerler.slice(0, 100);
      setMerkeziBildirimler(yeniDiger);
      setMerkeziChatBildirimler(chatOlanlar.slice(0, 100));
      // Gerçek veri gelince cache'e yaz
      if (tumBildirimler.length > 0) writeNotifCache(yeniDiger);
    };

    const unsubs = familyIds.map((fid) =>
      bildirimleriDinleService(fid, userUid, (items) => {
        snapshotsByFamily.set(fid, items);
        refreshMergedNotifications();
      })
    );

    return () => {
      unsubs.forEach((unsub) => { if (typeof unsub === "function") unsub(); });
    };
  };

  return {
    inAppNotifs, bildirimPopupAcik, setBildirimPopupAcik,
    bildirimChatRoomId, setBildirimChatRoomId,
    merkeziBildirimler, setMerkeziBildirimler,
    merkeziChatBildirimler, setMerkeziChatBildirimler,
    hatirlaticiGoster, setHatirlaticiGoster,
    popupBildirimler, setPopupBildirimler,
    bildirimGoster, bildirimKaldir,
    chatOdaBildirimleriniTemizle, bildirimdenGit,
    pushUrlYonlendir, bildirimlereDinle,
    bildirimEkleService, bildirimHerkeseYazService,
  };
}
